import React, { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';
import {
  MenuItemProps,
  SiteAccessBadge,
  EllipsisDropdownButton,
  getFixedWidthFaIcon,
} from '@odin-labs/components';
import {
  CreateContractorStripeInvoiceMutation,
  useCreateContractorStripeInvoiceMutation,
} from 'apollo/generated/client-operations';
import {
  ensureNonEmptyItems,
  getCurrentDomain,
  getFormattedPhoneNumber,
} from 'utils';
import { getGraphQLError } from 'utils/error';
import { getPrettyFormattedDate, isAfterToday } from 'utils/dates';
import { AuthContext } from 'auth';
import { to } from 'acl';
import {
  faEdit,
  faFileInvoiceDollar,
  faTrash,
  faWrench,
  faCreditCard,
  UserMinusIcon,
  UserPlusIcon,
} from 'components/icons';
import { AlertService } from 'components/alertService';
import { CardTitle } from 'components/reactstrap';
import { CopyLink } from 'components/copyLink';
import {
  useWorkerOnboardingLink,
  WorkerOnboardingLink,
} from 'components/workerOnboardingLink';
import { ContractorJobsiteDetail } from './ContractorJobsiteDetail';
import {
  ContractorJobsiteCardLoadingProps,
  ContractorJobsiteCardProps,
} from './types';
import { ParentJobsiteContractorLink } from './ParentJobsiteContractorLink';
import { SiteAccessText } from './SiteAccessText';

const classes = {
  skeleton: cn(
    'odin-text-transparent odin-animate-pulse odin-bg-gray-300 odin-rounded-md -odin-ml-1',
  ),
  actionsSkeleton: cn(
    'odin-animate-pulse odin-bg-gray-300 odin-h-8.5 odin-w-8.5 odin-rounded-md -odin-ml-1',
  ),
};

const EditIcon = getFixedWidthFaIcon({ icon: faEdit });
const WrenchIcon = getFixedWidthFaIcon({ icon: faWrench });
const TrashIcon = getFixedWidthFaIcon({ icon: faTrash });
const InvoiceIcon = getFixedWidthFaIcon({ icon: faFileInvoiceDollar });
const PaymentIcon = getFixedWidthFaIcon({ icon: faCreditCard });

export function ContractorJobsiteCard(
  props: ContractorJobsiteCardProps | ContractorJobsiteCardLoadingProps,
): ReactElement {
  const {
    contractor,
    jobsiteContractor,
    disableActions,
    openModal,
    onJobsiteInvitationCreated,
    loading,
  } = props as ContractorJobsiteCardProps;

  const { currentUser: user } = React.useContext(AuthContext);

  const hasAccess =
    !jobsiteContractor?.endDate || isAfterToday(jobsiteContractor.endDate);

  const location = useLocation();
  const baseUrl = location.pathname;

  const paymentUrlParts = [
    'contractor',
    contractor?.contractorId,
    'payment-methods',
  ];
  const paymentsOnboardingLink = new URL(
    paymentUrlParts.join('/'),
    getCurrentDomain(),
  ).toString();

  const {
    jobsiteInvitationId,
    onboardingLink,
    canGenerateOnboardingLink,
    generateOnboardingLink,
    isGeneratingOnboardingLink,
  } = useWorkerOnboardingLink({
    jobsiteInvitationId:
      jobsiteContractor?.jobsiteInvitation?.jobsiteInvitationId,
    jobsiteContractorId: jobsiteContractor?.id,
    onJobsiteInvitationCreated,
  });

  const [createContractorStripeInvoice] =
    useCreateContractorStripeInvoiceMutation({
      variables: {
        input: {
          contractorId: contractor?.contractorId,
          jobsiteContractorId: jobsiteContractor?.id,
        },
      },
      onCompleted: (result: CreateContractorStripeInvoiceMutation): void => {
        AlertService.alert(
          'success',
          'Success',
          'Invoice generated in Stripe, opening in new tab',
        );
        if (result?.createContractorStripeInvoice?.url) {
          window.open(result.createContractorStripeInvoice.url, '_blank');
        }
      },
      onError: (error): void => {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      },
    });

  const menuItems = ensureNonEmptyItems<MenuItemProps>([
    user.isAllowed(to.editContractorJobsiteAssignments) && {
      icon: EditIcon,
      text: 'Edit Assignment',
      theme: 'secondary',
      onClick: (): void => {
        openModal({ jobsiteContractor, modalAction: 'edit' });
      },
    },
    user.isAllowed(to.manageWorkerOnboardingLinks) &&
      (jobsiteInvitationId
        ? {
            icon: TrashIcon,
            text: 'Delete worker onboarding link',
            theme: 'danger',
            onClick: (): void => {
              openModal({
                jobsiteContractor,
                modalAction: 'removeOnboardingLink',
              });
            },
          }
        : {
            icon: WrenchIcon,
            text: 'Generate worker onboarding link',
            theme: 'secondary',
            onClick: generateOnboardingLink,
          }),
    user.isAllowed(to.removeContractorJobsiteAssignments) && {
      icon: TrashIcon,
      text: 'Delete Assignment',
      theme: 'danger',
      onClick: (): void => {
        openModal({ jobsiteContractor, modalAction: 'delete' });
      },
    },
    user.isAllowed(to.editContractorPayments, contractor?.contractorId) && {
      icon: PaymentIcon,
      text: 'Manage payment method',
      theme: 'secondary',
      onClick: (): void => {
        openModal({ jobsiteContractor, modalAction: 'editPaymentMethod' });
      },
    },
    user.isAllowed(to.generateContractorInvoices, contractor?.contractorId) && {
      icon: InvoiceIcon,
      text: 'Generate stripe invoice',
      theme: 'secondary',
      onClick: (): void => {
        createContractorStripeInvoice();
      },
    },
    user.isAllowed(
      to.offboardContractors,
      jobsiteContractor?.jobsite.jobsiteId,
    ) && {
      icon: UserMinusIcon,
      text: 'Offboard all workers',
      theme: 'secondary',
      onClick: (): void => {
        openModal({ jobsiteContractor, modalAction: 'offboard' });
      },
    },
    user.isAllowed(
      to.reinstateContractors,
      jobsiteContractor?.jobsite.jobsiteId,
    ) && {
      icon: UserPlusIcon,
      text: 'Reinstate all workers',
      theme: 'secondary',
      onClick: (): void => {
        openModal({ jobsiteContractor, modalAction: 'reinstate' });
      },
    },
  ]);

  const { firstName, lastName, title, email, phoneNumber } =
    jobsiteContractor?.contact ?? {};
  const contactName = [firstName, lastName].filter(Boolean).join(' ');
  const jobsiteContactText = [contactName, title].filter(Boolean).join(', ');
  const emailLink = email && (
    <a href={`mailto:${email}`} target="blank">
      {email}
    </a>
  );
  const phoneNumberLink = phoneNumber && (
    <a href={`tel:${phoneNumber}`}>{getFormattedPhoneNumber(phoneNumber)}</a>
  );

  const { jobsiteId, name: jobsiteName } = jobsiteContractor?.jobsite ?? {};
  const jobsiteLink = <Link to={`/jobsite/${jobsiteId}`}>{jobsiteName}</Link>;

  return (
    <div className="odin-flex odin-gap-x-4 odin-border odin-shadow-odin-lite odin-rounded-md odin-p-6">
      <div className="odin-grow odin-grid odin-grid-cols-1 odin-gap-y-4 sm:odin-grid-cols-3 sm:odin-gap-x-3">
        <div className="odin-space-y-3">
          <CardTitle as="h1" className="odin-mt-1">
            <span className={cn(loading && classes.skeleton)}>
              {loading ? 'Loading Jobsite Data' : jobsiteLink}
            </span>
            {user.isAllowed(
              to.editContractorPayments,
              contractor?.contractorId,
            ) &&
              jobsiteContractor?.stripePaymentMethodId && (
                <span
                  onClick={(): void => {
                    openModal({
                      jobsiteContractor,
                      modalAction: 'editPaymentMethod',
                    });
                  }}
                  className="odin-ml-3 odin-gap-x-3 odin-text-base"
                >
                  <PaymentIcon />
                </span>
              )}
          </CardTitle>
          <SiteAccessBadge
            loading={loading}
            className="odin-grow-0"
            status={hasAccess ? 'allowed' : 'completed'}
          />
          <SiteAccessText
            hasAccess={hasAccess}
            endDate={jobsiteContractor?.endDate}
          />
        </div>
        <div className="odin-col-span-2 odin-flex odin-flex-col odin-gap-y-3">
          <ContractorJobsiteDetail label="Prime Contractor" loading={loading}>
            <ParentJobsiteContractorLink
              parentContractor={
                jobsiteContractor?.parentJobsiteContractor?.contractor
              }
            />
          </ContractorJobsiteDetail>
          <ContractorJobsiteDetail label="Subcontractors" loading={loading}>
            <Link to={`${baseUrl}/subcontractors`}>
              {jobsiteContractor?.subcontractedJobsiteContractors.count}{' '}
              Subcontractors
            </Link>
          </ContractorJobsiteDetail>
          <ContractorJobsiteDetail label="Workers" loading={loading}>
            <Link to={`${baseUrl}/workers?jobsiteIds=${jobsiteId}`}>
              {jobsiteContractor?.jobsiteWorkers.count} Workers
            </Link>
          </ContractorJobsiteDetail>
          <ContractorJobsiteDetail
            label="Start Date"
            value={getPrettyFormattedDate(jobsiteContractor?.startDate)}
            loading={loading}
          />
          {jobsiteContractor?.endDate && (
            <ContractorJobsiteDetail
              label="End Date"
              value={getPrettyFormattedDate(jobsiteContractor?.endDate)}
              loading={loading}
            />
          )}
          <ContractorJobsiteDetail
            label="Worker Onboarding Link"
            loading={loading || isGeneratingOnboardingLink}
          >
            <WorkerOnboardingLink
              onboardingLink={onboardingLink}
              canGenerateOnboardingLink={canGenerateOnboardingLink}
              generateOnboardingLink={generateOnboardingLink}
              isGeneratingOnboardingLink={isGeneratingOnboardingLink}
              contractorName={contractor?.organization?.name}
              jobsiteName={jobsiteName}
            />
          </ContractorJobsiteDetail>

          {user.isAllowed(
            to.editContractorPayments,
            contractor?.contractorId,
          ) && (
            <ContractorJobsiteDetail
              label="Payment onboarding link"
              loading={loading}
            >
              <div className="odin-flex odin-gap-x-10">
                <CopyLink link={paymentsOnboardingLink} />
              </div>
            </ContractorJobsiteDetail>
          )}
          <div className="odin-border-t odin-border-gray-200 odin-my-3" />
          <ContractorJobsiteDetail
            label="Jobsite Contact"
            loading={loading}
            value={jobsiteContactText}
          />
          <ContractorJobsiteDetail
            label="Email Address"
            loading={loading}
            value={emailLink}
          />
          <ContractorJobsiteDetail
            label="Phone Number"
            loading={loading}
            value={phoneNumberLink}
          />
        </div>
      </div>
      <div>
        {disableActions !== true &&
          (loading ? (
            <div className={classes.actionsSkeleton} />
          ) : (
            menuItems.length > 0 && (
              <EllipsisDropdownButton
                menuItems={menuItems}
                popupWidthFitContent
              />
            )
          ))}
      </div>
    </div>
  );
}
